import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

interface AgentConfigResponse {
  model: string;
  maxTokens: number;
  hasApiKey: boolean;
  provider?: string;
}

interface ProviderInfo {
  id: string;
  displayName: string;
  command: string;
}

@customElement('cp-agent-settings')
export class CpAgentSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _successMessage = '';
  @state() private _apiKey = '';
  @state() private _model = 'claude-sonnet-4-6';
  @state() private _maxTokens = 4096;
  @state() private _hasApiKey = false;
  @state() private _setupRequired = false;
  @state() private _provider = 'anthropic';
  @state() private _providers: ProviderInfo[] = [];
  @state() private _modelsForProvider: string[] = [];

  connectedCallback(): void {
    super.connectedCallback();
    this._loadConfig();
  }

  private async _loadConfig(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const [configRes, providersRes] = await Promise.all([
        fetch('/api/agent-config', { credentials: 'include' }),
        fetch('/api/providers', { credentials: 'include' }),
      ]);

      if (configRes.status === 404) {
        this._setupRequired = true;
      } else if (!configRes.ok) {
        throw new Error('Fehler beim Laden der Agent-Konfiguration');
      } else {
        const data = await configRes.json() as AgentConfigResponse;
        this._model = data.model;
        this._maxTokens = data.maxTokens;
        this._hasApiKey = data.hasApiKey;
        this._provider = data.provider ?? 'anthropic';
        this._setupRequired = false;
      }

      if (providersRes.ok) {
        this._providers = await providersRes.json() as ProviderInfo[];
      }

      await this._loadModelsForProvider(this._provider);
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._loading = false;
    }
  }

  private async _loadModelsForProvider(providerId: string): Promise<void> {
    try {
      const res = await fetch(`/api/providers/${encodeURIComponent(providerId)}/models`, { credentials: 'include' });
      if (res.ok) {
        this._modelsForProvider = await res.json() as string[];
      }
    } catch {
      this._modelsForProvider = [];
    }
  }

  private _onApiKeyInput(e: Event): void {
    this._apiKey = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private async _onProviderChange(e: Event): Promise<void> {
    const newProvider = (e.target as HTMLSelectElement).value;
    this._provider = newProvider;
    this._successMessage = '';

    await this._loadModelsForProvider(newProvider);

    if (this._modelsForProvider.length > 0 && !this._modelsForProvider.includes(this._model)) {
      this._model = this._modelsForProvider[0];
    }
  }

  private _onModelChange(e: Event): void {
    this._model = (e.target as HTMLSelectElement).value;
    this._successMessage = '';
  }

  private _onMaxTokensInput(e: Event): void {
    const val = Number((e.target as HTMLInputElement).value);
    if (!Number.isNaN(val) && val > 0) {
      this._maxTokens = val;
    }
    this._successMessage = '';
  }

  private async _save(): Promise<void> {
    if (this._saving) return;

    this._saving = true;
    this._error = '';
    this._successMessage = '';

    const body: Record<string, unknown> = {
      model: this._model,
      maxTokens: this._maxTokens,
      provider: this._provider,
    };
    if (this._apiKey.trim()) {
      body.apiKey = this._apiKey.trim();
    }

    try {
      const res = await fetch('/api/agent-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Fehler beim Speichern' }));
        throw new Error(errData.error || 'Fehler beim Speichern');
      }
      const data = await res.json() as AgentConfigResponse;
      this._model = data.model;
      this._maxTokens = data.maxTokens;
      this._hasApiKey = data.hasApiKey;
      if (data.provider) {
        this._provider = data.provider;
      }
      this._apiKey = '';
      this._setupRequired = false;
      this._successMessage = 'Agent-Konfiguration gespeichert';
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._saving = false;
    }
  }

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      this._save();
    }
  }

  private _renderStatus() {
    if (this._setupRequired) {
      return html`<span class="cp-agent__status cp-agent__status--not-configured">Ersteinrichtung</span>`;
    }
    if (this._hasApiKey) {
      return html`<span class="cp-agent__status cp-agent__status--configured">Konfiguriert</span>`;
    }
    return html`<span class="cp-agent__status cp-agent__status--not-configured">Kein API Key</span>`;
  }

  render() {
    return html`
      <div class="cp-agent">
        <div class="cp-agent__header">
          <h3 class="cp-agent__title">Agent</h3>
          ${this._renderStatus()}
        </div>

        ${this._error ? html`
          <div class="cp-agent__error">${this._error}</div>
        ` : nothing}

        ${this._successMessage ? html`
          <div class="cp-agent__success">${this._successMessage}</div>
        ` : nothing}

        ${this._loading ? html`
          <p class="cp-agent__loading">Konfiguration wird geladen...</p>
        ` : html`
          ${this._setupRequired ? html`
            <div class="cp-agent__setup-hint">
              Richte deinen Agent ein, um automatische Tasks auszufuehren.
              Du benoetigst einen eigenen API Key.
            </div>
          ` : nothing}

          <div class="cp-agent__form">
            <div class="cp-agent__field">
              <label class="cp-agent__label" for="agent-api-key">API Key</label>
              <input
                id="agent-api-key"
                class="cp-agent__input"
                type="password"
                placeholder=${this._hasApiKey ? 'Bereits konfiguriert - leer lassen um beizubehalten' : 'API Key eingeben'}
                .value=${this._apiKey}
                @input=${this._onApiKeyInput}
                @keydown=${this._onKeyDown}
              />
            </div>

            <div class="cp-agent__field">
              <label class="cp-agent__label" for="agent-provider">Provider</label>
              <select
                id="agent-provider"
                class="cp-agent__select"
                .value=${this._provider}
                @change=${this._onProviderChange}
              >
                ${this._providers.map(p => html`
                  <option value=${p.id} ?selected=${this._provider === p.id}>${p.displayName}</option>
                `)}
              </select>
            </div>

            <div class="cp-agent__field">
              <label class="cp-agent__label" for="agent-model">Modell</label>
              <select
                id="agent-model"
                class="cp-agent__select"
                .value=${this._model}
                @change=${this._onModelChange}
              >
                ${this._modelsForProvider.map(m => html`
                  <option value=${m} ?selected=${this._model === m}>${m}</option>
                `)}
              </select>
            </div>

            <div class="cp-agent__field">
              <label class="cp-agent__label" for="agent-max-tokens">Max Tokens</label>
              <input
                id="agent-max-tokens"
                class="cp-agent__input cp-agent__input--number"
                type="number"
                min="1"
                max="200000"
                .value=${String(this._maxTokens)}
                @input=${this._onMaxTokensInput}
                @keydown=${this._onKeyDown}
              />
            </div>

            <button
              class="cp-agent__btn cp-agent__btn--save"
              @click=${this._save}
              ?disabled=${this._saving}
            >${this._saving ? 'Speichert...' : 'Speichern'}</button>
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-agent-settings': CpAgentSettings;
  }
}
